import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DialogPresets } from '@principle-theorem/ng-shared';
import {
  HicapsConnectMerchant,
  Invoice,
  Practice,
} from '@principle-theorem/principle-core';
import {
  IBridgeDevice,
  IHealthcareClaim,
  IHicapsConnectMerchant,
  IHicapsConnectTerminalV2,
  IInvoice,
  IPractice,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  INamedDocument,
  IReffable,
  WithRef,
  firstValueFrom,
  undeletedFilter,
} from '@principle-theorem/shared';
import { compact, first, sortBy } from 'lodash';
import { ISelectedHicapsConnectTerminal } from '../selected-hicaps-connect-terminal';
import { HicapsConnectV2Adapter } from './hicaps-connect-v2-adapter';
import { HicapsConnectV2Routing } from './hicaps-connect-v2-routing';
import {
  HicapsConnectV2SelectTerminalDialogComponent,
  IHicapsV2SelectTerminalRequest,
  IHicapsV2SelectTerminalResponse,
} from './hicaps-connect-v2-select-terminal-dialog/hicaps-connect-v2-select-terminal-dialog.component';
import {
  HicapsConnectV2TerminalMerchants,
  IHicapsConnectV2TerminalMerchant,
} from './hicaps-connect-v2-terminal-merchants';

const AUTO_SELECT_NOTICE_MS = 3000;

interface IHicapsConnectV2SelectionData {
  terminals: WithRef<IHicapsConnectTerminalV2>[];
  merchants: WithRef<IHicapsConnectMerchant>[];
  bridgeDevices: WithRef<IBridgeDevice>[];
}

interface IHicapsConnectV2SelectionCandidate {
  terminal: ISelectedHicapsConnectTerminal;
  merchant?: WithRef<IHicapsConnectMerchant>;
  bridgeName: string;
}

@Injectable({ providedIn: 'root' })
export class HicapsConnectV2TerminalSelectorService {
  private readonly _dialog = inject(MatDialog);
  private readonly _snackBar = inject(MatSnackBar);

  async selectTerminal(
    invoice: Pick<IInvoice, 'practice' | 'items' | 'claims'>,
    claim?: IHealthcareClaim
  ): Promise<ISelectedHicapsConnectTerminal | undefined> {
    const selectionData = await this._loadSelectionData(invoice.practice);
    const terminalMerchants = this._buildTerminalMerchants(selectionData);
    const candidates = this._buildCandidates(terminalMerchants, selectionData);
    const practitionersOnInvoice = this._getPractitionersOnInvoice(
      invoice,
      claim
    );
    const filteredCandidates = this._filterCandidatesForPractitioners(
      candidates,
      practitionersOnInvoice
    );

    const autoSelected = first(filteredCandidates);
    if (autoSelected && filteredCandidates.length === 1) {
      this._snackBar.open(
        `Using ${autoSelected.terminal.name} via ${autoSelected.bridgeName}`,
        'OK',
        { duration: AUTO_SELECT_NOTICE_MS }
      );
      return autoSelected.terminal;
    }

    return this._openV2TerminalDialog(
      terminalMerchants,
      selectionData,
      practitionersOnInvoice,
      invoice.practice
    );
  }

  private async _loadSelectionData(
    practice: IReffable<IPractice>
  ): Promise<IHicapsConnectV2SelectionData> {
    const [terminals, merchants, bridgeDevices] = await Promise.all([
      Firestore.getDocs(Practice.hicapsConnectTerminalV2Col(practice)),
      Firestore.getDocs(Practice.hicapsConnectMerchantCol(practice)),
      Firestore.getDocs(Practice.bridgeDeviceCol(practice)),
    ]);

    return {
      terminals: terminals.filter(undeletedFilter),
      merchants: merchants.filter(undeletedFilter),
      bridgeDevices,
    };
  }

  private _buildTerminalMerchants(
    selectionData: IHicapsConnectV2SelectionData
  ): IHicapsConnectV2TerminalMerchant[] {
    return HicapsConnectV2TerminalMerchants.fromTerminals(
      selectionData.terminals,
      selectionData.merchants
    );
  }

  private _buildCandidates(
    terminalMerchants: IHicapsConnectV2TerminalMerchant[],
    selectionData: IHicapsConnectV2SelectionData
  ): IHicapsConnectV2SelectionCandidate[] {
    return compact(
      terminalMerchants.map((terminalMerchant) =>
        this._buildCandidate(terminalMerchant, selectionData)
      )
    );
  }

  private _buildCandidate(
    terminalMerchant: IHicapsConnectV2TerminalMerchant,
    selectionData: IHicapsConnectV2SelectionData
  ): IHicapsConnectV2SelectionCandidate | undefined {
    const route = HicapsConnectV2Routing.resolveRoute(
      terminalMerchant.terminal,
      selectionData.bridgeDevices
    );

    if (!route) {
      return;
    }

    return {
      terminal: HicapsConnectV2Adapter.toSelectedTerminal(
        terminalMerchant.terminal,
        terminalMerchant.merchantId,
        terminalMerchant.merchant,
        route.chosen
      ),
      merchant: terminalMerchant.merchant,
      bridgeName: route.chosen.bridgeDevice.name,
    };
  }

  private _filterCandidatesForPractitioners(
    candidates: IHicapsConnectV2SelectionCandidate[],
    practitionersOnInvoice: IReffable<IStaffer>[]
  ): IHicapsConnectV2SelectionCandidate[] {
    return candidates.filter((candidate) =>
      HicapsConnectMerchant.isAvailableToPractitioners(
        candidate.merchant,
        practitionersOnInvoice
      )
    );
  }

  private _getPractitionersOnInvoice(
    invoice: Pick<IInvoice, 'practice' | 'items' | 'claims'>,
    claim?: IHealthcareClaim
  ): IReffable<IStaffer>[] {
    return claim ? [claim.practitioner] : Invoice.staffOnInvoice(invoice);
  }

  private _getDialogTerminalMerchants(
    terminalMerchants: IHicapsConnectV2TerminalMerchant[],
    practitionersOnInvoice: IReffable<IStaffer>[]
  ): IHicapsConnectV2TerminalMerchant[] {
    return terminalMerchants.filter((terminalMerchant) =>
      HicapsConnectMerchant.isAvailableToPractitioners(
        terminalMerchant.merchant,
        practitionersOnInvoice
      )
    );
  }

  private async _openV2TerminalDialog(
    terminalMerchants: IHicapsConnectV2TerminalMerchant[],
    selectionData: IHicapsConnectV2SelectionData,
    practitionersOnInvoice: IReffable<IStaffer>[],
    practice: INamedDocument<IPractice>
  ): Promise<ISelectedHicapsConnectTerminal | undefined> {
    const dialogTerminalMerchants = sortBy(
      this._getDialogTerminalMerchants(
        terminalMerchants,
        practitionersOnInvoice
      ),
      (terminalMerchant) => terminalMerchant.terminal.name
    );

    const dialogRef = this._dialog.open<
      HicapsConnectV2SelectTerminalDialogComponent,
      IHicapsV2SelectTerminalRequest,
      IHicapsV2SelectTerminalResponse
    >(
      HicapsConnectV2SelectTerminalDialogComponent,
      DialogPresets.medium({
        data: {
          terminalMerchants: dialogTerminalMerchants,
          bridgeDevices: selectionData.bridgeDevices,
          practice,
          practitionersOnInvoice,
        },
      })
    );
    const response = await firstValueFrom(dialogRef.afterClosed());

    return response?.terminal;
  }
}
